// useJobStream(id, job?) → JobStream        (DESIGN_SPEC §12.1–12.2)
// The job's SSE stream folded into an external store, plus the effective lifecycle derived from
// it: state (latest job.state event; the record before any event, or when the record is terminal
// and the stream is not), verdict (latest verdict event, else the record's), T0, transitions,
// live/ended, batchId, rate, lastSeq, connection. live && !ended → cockpit; !live && ended →
// document; !live && !ended → the record is final but the stream stopped short (partial).
// One connection per job however many components call this (streamRegistry). Without `job`,
// the record is read from React Query ["job", id] (shared with useJob, polled while live).
// Memoize derivations on stream.snap.version; read stream.rate under useClock.
import { useEffect, useMemo, useReducer, useSyncExternalStore } from "react";
import { deriveEffective } from "@/lib/eventStore";
import type { JobStream } from "@/lib/derive/types";
import type { Job } from "@/types";
import { useJob } from "./useJob";
import { streams } from "./streamRegistry";

/** "record syncing" appears once the record has disagreed with the stream for this long. */
const RECORD_LAG_MS = 10_000;

export function useJobStream(id: string, job?: Job): JobStream {
  const entry = useMemo(() => streams.entryFor(id), [id]);
  const recordQ = useJob(id, { enabled: job === undefined });
  const record = job ?? recordQ.data;

  useEffect(() => {
    streams.acquire(entry);
    return () => streams.release(entry);
  }, [entry]);

  useEffect(() => {
    streams.setRecord(entry, record);
  }, [entry, record]);

  const snap = useSyncExternalStore(entry.store.subscribe, entry.store.getSnapshot, entry.store.getSnapshot);
  const eff = useMemo(() => deriveEffective(snap, record), [snap, record]);

  // recordLags turns on RECORD_LAG_MS after the stream moved past the record.
  const since = eff.disagree?.since;
  const lagAt = since !== undefined && Number.isFinite(since) ? since + RECORD_LAG_MS : null;
  const [lagTick, bump] = useReducer((n: number) => n + 1, 0);
  const lags = lagAt !== null && Date.now() >= lagAt;
  useEffect(() => {
    if (lagAt === null || lags) return;
    const t = setTimeout(bump, Math.max(0, lagAt - Date.now()) + 50);
    return () => clearTimeout(t);
  }, [lagAt, lags, lagTick]);

  return useMemo<JobStream>(
    () => ({
      snap,
      state: eff.state,
      stateSource: eff.stateSource,
      recordLags: lags,
      recordState: record?.state,
      reason: eff.reason,
      verdict: eff.verdict,
      live: eff.live,
      ended: eff.ended,
      t0: eff.t0,
      t0Source: eff.t0Source,
      transitions: eff.transitions,
      batchId: snap.batchId,
      rate: entry.store.rate,
      connection: snap.connection,
      lastSeq: snap.lastSeq,
      retry: snap.retry,
      reconnect: entry.reconnect,
    }),
    [snap, eff, lags, record?.state, entry],
  );
}
